// Multi-territory Upload API service
// Integrates with wht-mt-data-management backend APIs for file upload operations.
// All calls require X-Country-Code header.

import {
  MT_BASE_URL,
  MT_UPLOAD_DELETE_ENTRY_DATA_API,
  MT_UPLOAD_DOWNLOAD_ERROR_FILE_API,
  MT_UPLOAD_DOWNLOAD_FILE_META_DATA_API,
  MT_UPLOAD_DOWNLOAD_ORIGINAL_FILE_API,
  MT_UPLOAD_DOWNLOAD_TEMPLATE_API,
  MT_UPLOAD_FILE_API,
  MT_UPLOAD_FILE_DATA_TYPE_API,
  MT_UPLOAD_FILE_SOURCE_API,
  MT_UPLOAD_GET_FILE_META_DATA_API,
  MT_UPLOAD_SUMMARY_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
  mtBuildRequestHeader,
  mtHandleErrorResponse,
  mtHandleRequestFailure,
  mtParseJsonResponse,
  mtUrlBuilder,
  type MTApiResponse,
} from '@/multi-territory/mt-services/mt-api-utils'
import type {
  MTDeleteEntryPayload,
  MTDownloadOriginalFilePayload,
  MTUploadDataType,
  MTUploadMetaDataRequest,
  MTUploadMetaDataResponse,
  MTUploadSource,
  MTUploadSummaryRequest,
} from '@/multi-territory/mt-types/mt-upload'
import { getAccessToken } from '@/utils/session'

// --- Helper: Build upload headers with country code ---

function buildUploadHeaders(countryCode: string): Record<string, string> {
  return {
    'X-Country-Code': countryCode,
  }
}

// --- API Functions ---

/**
 * Fetches available file sources for the upload dropdown.
 * GET /wht-mt-data-management/api/v1/upload/enums/file-source
 */
export async function fetchMTFileSource(
  countryCode: string
): Promise<MTApiResponse<MTUploadSource[]>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_FILE_SOURCE_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('GET', null, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<MTUploadSource[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetches available file data types for the upload dropdown.
 * GET /wht-mt-data-management/api/v1/upload/enums/file-data-type
 */
export async function fetchMTFileDataType(
  countryCode: string
): Promise<MTApiResponse<MTUploadDataType[]>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_FILE_DATA_TYPE_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('GET', null, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<MTUploadDataType[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetches file metadata list with pagination and search.
 * POST /wht-mt-data-management/api/v1/upload/get/file-meta-data
 */
export async function fetchMTFileMetaData(
  countryCode: string,
  payload: MTUploadMetaDataRequest
): Promise<MTApiResponse<MTUploadMetaDataResponse>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_GET_FILE_META_DATA_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<MTUploadMetaDataResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Downloads file metadata as Excel file.
 * POST /wht-mt-data-management/api/v1/upload/download/file-meta-data
 */
export async function downloadMTFileMetaData(
  countryCode: string,
  payload: MTUploadMetaDataRequest
): Promise<MTApiResponse<Blob>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_DOWNLOAD_FILE_META_DATA_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Downloads the original uploaded file(s).
 * POST /wht-mt-data-management/api/v1/upload/download/original-file
 */
export async function downloadMTOriginalFile(
  countryCode: string,
  payload: MTDownloadOriginalFilePayload[]
): Promise<MTApiResponse<Blob>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_DOWNLOAD_ORIGINAL_FILE_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Downloads error file for failed/partially uploaded items.
 * POST /wht-mt-data-management/api/v1/upload/download/error-file
 * Payload: array of loadId strings
 */
export async function downloadMTErrorFile(
  countryCode: string,
  loadIds: string[]
): Promise<MTApiResponse<{ data: unknown[]; success: boolean }>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_DOWNLOAD_ERROR_FILE_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('POST', loadIds, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<{ data: unknown[]; success: boolean }>(
      response
    )
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Deletes upload entry data.
 * POST /wht-mt-data-management/api/v1/upload/delete/entry-data
 */
export async function deleteMTEntryData(
  countryCode: string,
  entries: MTDeleteEntryPayload[]
): Promise<MTApiResponse<string>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_DELETE_ENTRY_DATA_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('POST', entries, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const json = await response.json()
    if (json && json.success === false && json.errors) {
      const errorMsg = json.errors.map((e: any) => e.message).join(', ')
      return { success: false, error: errorMsg }
    }

    return { success: true, data: json?.data || 'Entries deleted successfully' }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Downloads a template file by name.
 * POST /wht-mt-data-management/api/v1/upload/download-template
 */
export async function downloadMTTemplate(
  countryCode: string,
  templateName: string
): Promise<MTApiResponse<Blob>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_DOWNLOAD_TEMPLATE_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('POST', { templateName }, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Uploads a file with metadata.
 * POST /wht-mt-data-management/api/v1/fileUpload/upload
 * Uses multipart/form-data (no JSON Content-Type).
 */
export async function uploadMTFile(
  countryCode: string,
  params: {
    file: File | File[]
    source: string
    dataType: string
    category?: string | null
    loadType?: string
    transformId?: number | null
  }
): Promise<MTApiResponse<any>> {
  try {
    const {
      file,
      source,
      dataType,
      category = null,
      loadType = 'Web Based',
      transformId = null,
    } = params
    const token = getAccessToken()
    const baseUrl = MT_BASE_URL ?? ''
    const url = `${baseUrl}/${MT_UPLOAD_FILE_API}`

    // Category comes from the dataType API response (no hardcoded mapping)

    // Reload source sets transformId to -1
    let finalTransformId = transformId
    if (source === 'Reload') {
      finalTransformId = -1
    }

    const formData = new FormData()

    // Alt sources support multiple file uploads
    const isAltSource = source?.toLowerCase().startsWith('alt')
    if (isAltSource && Array.isArray(file)) {
      file.forEach(f => formData.append('file', f))
    } else {
      formData.append('file', Array.isArray(file) ? file[0] : file)
    }

    formData.append('source', source)
    if (category) formData.append('category', category)
    formData.append('dataType', dataType)
    formData.append('loadType', loadType)
    if (finalTransformId !== null) {
      formData.append('transformId', String(finalTransformId))
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: token ?? '',
        'X-Country-Code': countryCode,
      },
      body: formData,
    })

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const json = await response.json()
    if (json && json.success === false && json.errors) {
      const errorMsg = json.errors.map((e: any) => e.message).join(', ')
      return { success: false, error: errorMsg }
    }

    return { success: true, data: json }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetches upload summary.
 * POST /wht-mt-data-management/api/v1/fileUpload/summary
 */
export async function fetchMTUploadSummary(
  countryCode: string,
  payload: MTUploadSummaryRequest
): Promise<MTApiResponse<any>> {
  try {
    const url = mtUrlBuilder(MT_UPLOAD_SUMMARY_API)
    const extraHeaders = buildUploadHeaders(countryCode)
    const options = mtBuildRequestHeader('POST', payload, extraHeaders)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
